import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookingStatus } from '@fleet/shared';
import { bookingsApi, type CalendarEntry, type CalendarVehicle } from '../api/bookings';
import {
  DAY_END_HOUR,
  DAY_START_HOUR,
  dayFraction,
  dayTimeLabel,
  formatDayMonth,
  formatWeekday,
  isSameDay,
  localDateKey,
  weekDays,
  weekRange,
} from './calendar-geometry';

interface Props {
  week: Date;
  vehicleGroupId?: string;
  /** Leere Menge heißt: keine Einschränkung. */
  vehicleIds: Set<string>;
  onSelectBooking: (entry: CalendarEntry) => void;
  /** Sprung in die Tagesansicht — dort wird gebucht. */
  onSelectDay: (day: Date) => void;
}

/**
 * Wochen-Ressourcenansicht: Fahrzeuge als Zeilen, Tage als Spalten.
 *
 * Die Tagesansicht beantwortet „welches Fahrzeug ist um 14 Uhr frei", diese
 * hier die andere Hälfte der Frage: „an welchem Tag ist überhaupt etwas frei".
 * Deshalb bewusst keine Minutengenauigkeit — jede Zelle zeigt einen Balken über
 * dem Tagesfenster von 6 bis 22 Uhr, aus dem sich die Belegung ablesen lässt,
 * ohne sie ablesen zu müssen.
 *
 * Gebucht wird hier nicht. Ein Ziehen über eine Wochenspalte wäre auf
 * Viertelstunden nicht auflösbar, und eine Buchung mit falscher Uhrzeit ist
 * teurer als ein zusätzlicher Klick in die Tagesansicht.
 */
export function WeekCalendar({
  week,
  vehicleGroupId,
  vehicleIds,
  onSelectBooking,
  onSelectDay,
}: Props) {
  const { from, to } = useMemo(() => weekRange(week), [week]);
  const days = useMemo(() => weekDays(week), [week]);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['availability', from.toISOString(), to.toISOString(), vehicleGroupId ?? null],
    queryFn: () => bookingsApi.availability(from, to, vehicleGroupId),
  });

  const vehicles = (data?.vehicles ?? []).filter(
    (vehicle) => vehicleIds.size === 0 || vehicleIds.has(vehicle.id),
  );

  /** Belegungen je Fahrzeug und Kalendertag. */
  const entriesByCell = useMemo(() => {
    const map = new Map<string, CalendarEntry[]>();
    for (const entry of data?.entries ?? []) {
      const start = new Date(entry.startsAt);
      const end = new Date(entry.endsAt);

      // Eine mehrtägige Buchung erscheint in jeder betroffenen Spalte.
      for (const day of days) {
        if (localDateKey(day) < localDateKey(start) || localDateKey(day) > localDateKey(end)) {
          continue;
        }
        const key = `${entry.vehicleId}:${localDateKey(day)}`;
        const list = map.get(key) ?? [];
        list.push(entry);
        map.set(key, list);
      }
    }
    return map;
  }, [data, days]);

  if (isError) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white px-4 py-6">
        <p className="text-sm text-red-700">
          Belegungen konnten nicht geladen werden: {(error as Error).message}
        </p>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white px-4 py-6">
        <p className="text-sm text-slate-500">Wird geladen …</p>
      </div>
    );
  }

  if (vehicles.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
        Keine Fahrzeuge im gewählten Ausschnitt.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="w-full min-w-4xl border-collapse">
        <thead>
          <tr>
            <th className="sticky left-0 z-10 w-44 bg-white px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              Fahrzeug
            </th>
            {days.map((day) => {
              const today = isSameDay(day, new Date());
              return (
                <th key={day.toISOString()} className="border-l border-slate-200 px-2 py-2">
                  <button
                    type="button"
                    onClick={() => onSelectDay(day)}
                    className={[
                      'w-full rounded px-2 py-1 text-sm hover:bg-slate-100',
                      today ? 'bg-slate-900 text-white hover:bg-slate-800' : '',
                    ].join(' ')}
                    title="In die Tagesansicht wechseln"
                  >
                    <span className="block font-medium">{formatWeekday(day)}</span>
                    <span
                      className={['block text-xs tabular-nums', today ? 'text-slate-200' : 'text-slate-500'].join(' ')}
                    >
                      {formatDayMonth(day)}
                    </span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {vehicles.map((vehicle) => (
            <tr key={vehicle.id} className="border-t border-slate-100">
              <th
                scope="row"
                className="sticky left-0 z-10 bg-white px-3 py-2 text-left align-top font-normal"
              >
                <div className="flex items-center gap-1.5">
                  <span
                    className="size-2 shrink-0 rounded-full"
                    style={{ backgroundColor: vehicle.colorHex }}
                    aria-hidden
                  />
                  <span className={['text-sm font-medium', vehicle.canBook ? '' : 'text-slate-400'].join(' ')}>
                    {vehicle.licensePlate}
                  </span>
                </div>
                <div className="pl-3.5 text-xs text-slate-500">{vehicle.displayName}</div>
                {!vehicle.canBook && (
                  <div className="pl-3.5 text-xs text-slate-400">nicht buchbar</div>
                )}
              </th>

              {days.map((day) => (
                <DayCell
                  key={day.toISOString()}
                  day={day}
                  vehicle={vehicle}
                  entries={entriesByCell.get(`${vehicle.id}:${localDateKey(day)}`) ?? []}
                  onSelectBooking={onSelectBooking}
                  onSelectDay={onSelectDay}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DayCell({
  day,
  vehicle,
  entries,
  onSelectBooking,
  onSelectDay,
}: {
  day: Date;
  vehicle: CalendarVehicle;
  entries: CalendarEntry[];
  onSelectBooking: (entry: CalendarEntry) => void;
  onSelectDay: (day: Date) => void;
}) {
  const sorted = [...entries].sort((a, b) => a.startsAt.localeCompare(b.startsAt));

  return (
    <td className="border-l border-slate-200 px-2 py-2 align-top">
      {/* Belegungsbalken über dem Tagesfenster — die Antwort auf „wie voll". */}
      <button
        type="button"
        onClick={() => onSelectDay(day)}
        className="relative block h-2 w-full overflow-hidden rounded-full bg-slate-100"
        title={`${formatWeekday(day)} in der Tagesansicht öffnen (${DAY_START_HOUR}–${DAY_END_HOUR} Uhr)`}
      >
        {sorted.map((entry) => {
          const { left, width } = dayFraction(new Date(entry.startsAt), new Date(entry.endsAt), day);
          return (
            <span
              key={entry.id}
              className="absolute inset-y-0"
              style={{
                left: `${left * 100}%`,
                width: `${width * 100}%`,
                backgroundColor: vehicle.colorHex,
                opacity: entry.status === BookingStatus.PENDING_APPROVAL ? 0.45 : 1,
              }}
            />
          );
        })}
      </button>

      <ul className="mt-1 space-y-0.5">
        {sorted.slice(0, 3).map((entry) => (
          <li key={entry.id}>
            <button
              type="button"
              onClick={() => onSelectBooking(entry)}
              className={[
                'w-full truncate rounded px-1 py-0.5 text-left text-xs hover:bg-slate-100',
                entry.status === BookingStatus.PENDING_APPROVAL ? 'text-amber-700' : 'text-slate-600',
                entry.isOwn ? 'font-medium' : '',
              ].join(' ')}
              title={[
                dayTimeLabel(new Date(entry.startsAt), new Date(entry.endsAt), day),
                entry.driverName,
                entry.purpose,
              ]
                .filter(Boolean)
                .join(' · ')}
            >
              <span className="tabular-nums">
                {dayTimeLabel(new Date(entry.startsAt), new Date(entry.endsAt), day)}
              </span>
              <span className="ml-1">{entry.driverName}</span>
            </button>
          </li>
        ))}

        {sorted.length > 3 && (
          <li className="px-1 text-xs text-slate-400">+{sorted.length - 3} weitere</li>
        )}
      </ul>
    </td>
  );
}
